import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { PathRouteConstants } from 'src/app/shared/constants/app-route.constants';
import { LessonCurriculumApiService } from '../../services/lesson-curriculum-api.service';
import { LessonsApiService } from '../../services/lessons-api.service';
import { ApiDataService } from 'src/app/shared/services/api.service';


@Component({
  selector: 'app-edit-lesson-curriculum',
  templateUrl: './edit-lesson-curriculum.component.html',
  standalone: false
})
export class EditLessonCurriculumComponent implements OnInit, OnDestroy {
  ngUnsubscribe = new Subject<void>();
  curriculumId: number;
  lessonPlans: any[] = [];
  allLessonPlans: any[] = [];
  selectedLessonPlanId: number = null;
  loading = false;
  allCurriculumLessonPlans: any[] = [];
  allGrades: any[] = [];
  availableCurriculumGrades: any[] = [];
  selectedCurriculumGradeIds: number[] = [];
  lessonPlanSearchControl = new FormControl('');
  filteredLessonPlans: any[] = [];

  curriculumForm = new FormGroup({
    curriculum_name: new FormControl('', [Validators.required]),
    description: new FormControl(''),
    is_finished: new FormControl(false),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private navigationService: NavigationService,
    private lessonCurriculumApiService: LessonCurriculumApiService,
    private lessonsApiService: LessonsApiService,
    private toastrService: ToastrService,
    private apiService: ApiDataService,
  ) { }

  ngOnInit() {
    this.getGrades();
    this.route.params.subscribe(params => {
      this.curriculumId = +params['id'];
      this.getCurriculum();
    });
    this.getAllLessonPlans();
    this.lessonPlanSearchControl.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.filterLessonPlans());
  }

  getGrades() {
    this.apiService.getAllGrades()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.allGrades = res.sort((a, b) =>
            this.getGradeSortValue(a.grade_level) -
            this.getGradeSortValue(b.grade_level)
          );

          this.setAvailableCurriculumGrades();
        }
      });
  }

  getGradeSortValue(gradeLevel: string) {
    const gradeNumber = parseInt(gradeLevel, 10);
    return Number.isNaN(gradeNumber) ? Number.MAX_SAFE_INTEGER : gradeNumber;
  }

  setAvailableCurriculumGrades() {
    const hasFifthGrade = this.allCurriculumLessonPlans.some(lpc =>
      this.getGradeSortValue(lpc.lesson_plan_id?.grade_id?.grade_level) === 5
    );

    this.availableCurriculumGrades = this.allGrades.filter(grade => {
      const gradeNumber = this.getGradeSortValue(grade.grade_level);
      return gradeNumber >= 1 && gradeNumber <= 4 || (gradeNumber === 5 && hasFifthGrade);
    });

    const availableGradeIds = this.availableCurriculumGrades.map(grade => grade.id);
    this.selectedCurriculumGradeIds = this.selectedCurriculumGradeIds.filter(gradeId =>
      availableGradeIds.includes(gradeId)
    );
  }

  filterLessonPlans() {
    const searchTerm =
      (this.lessonPlanSearchControl.value || '').toLowerCase();

    this.filteredLessonPlans = this.allLessonPlans.filter(lp => {
      const matchesSearch =
        `${lp.lesson_plan_number || ''} ${lp.grade_id?.grade_level || ''} ${lp.lesson_plan_name || ''}`
          .toLowerCase()
          .includes(searchTerm);

      const matchesGrade =
        !this.selectedCurriculumGradeIds.length ||
        this.selectedCurriculumGradeIds.includes(Number(lp.grade_id?.id));

      return matchesSearch && matchesGrade;
    });
  }

  getCurriculum() {
    this.loading = true;

    this.lessonCurriculumApiService.getCurriculum(this.curriculumId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.curriculumForm.patchValue({
            curriculum_name: res.curriculum_name,
            description: res.description,
            is_finished: !!res.is_finished,
          });

          this.allCurriculumLessonPlans = res.lessonPlans ?? [];
          this.setAvailableCurriculumGrades();
          this.filterCurriculumLessonPlans();
        }

        this.loading = false;
      });
  }

  getAllLessonPlans() {
    this.lessonsApiService.getLessonNames()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.allLessonPlans = res;
          this.filterLessonPlans();
        }
      });
  }

  onSubmit() {
    if (this.curriculumForm.invalid) return;
    const payload = {
      id: this.curriculumId,
      ...this.curriculumForm.value,
      is_finished: this.curriculumForm.value.is_finished ? 1 : 0,
    };
    this.lessonCurriculumApiService.updateCurriculum(payload)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: res => {
        if (res) {
          this.toastrService.success('Yearly Plan updated');
          this.navigateBack();
        } else {
          this.toastrService.error('Something went wrong');
        }
        },
        error: error => {
          this.toastrService.error(error?.error?.message || 'Something went wrong');
        },
      });
  }

  addLessonPlan() {
    if (!this.selectedLessonPlanId) return;
    this.lessonCurriculumApiService.addLessonPlanToCurriculum({
      lesson_plan_id: this.selectedLessonPlanId,
      curriculum_name: this.curriculumForm.value.curriculum_name,
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.selectedLessonPlanId = null;
          this.getCurriculum();
          this.toastrService.success('Lesson plan added');
        } else {
          this.toastrService.error('Failed to add lesson plan');
        }
      });
  }

  filterCurriculumLessonPlans() {
  if (!this.selectedCurriculumGradeIds.length) {
    this.lessonPlans = [...this.allCurriculumLessonPlans];
  } else {
    this.lessonPlans = this.allCurriculumLessonPlans.filter(lpc =>
      this.selectedCurriculumGradeIds.includes(
        Number(lpc.lesson_plan_id?.grade_id?.id)
      )
    );
  }

  this.lessonPlans.sort((a, b) => {
    const gradeA = this.getGradeSortValue(
      a.lesson_plan_id?.grade_id?.grade_level
    );

    const gradeB = this.getGradeSortValue(
      b.lesson_plan_id?.grade_id?.grade_level
    );

    if (gradeA !== gradeB) {
      return gradeA - gradeB;
    }

    const lessonA = Number(
      a.lesson_plan_id?.lesson_plan_number ?? 0
    );

    const lessonB = Number(
      b.lesson_plan_id?.lesson_plan_number ?? 0
    );

    return lessonA - lessonB;
  });

  this.filterLessonPlans();
}

  removeLessonPlan(id: number) {
    this.lessonCurriculumApiService.removeLessonPlanFromCurriculum(id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.lessonPlans = this.lessonPlans.filter(lp => lp.id !== id);
          this.toastrService.success('Lesson plan removed');
        } else {
          this.toastrService.error('Failed to remove lesson plan');
        }
      });
  }

  navigateBack() {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
      return;
    }

    this.navigationService.navigateToPageAsync([PathRouteConstants.LESSONS], {
      queryParams: { tab: 'curriculum' },
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }
}
